import PropTypes from 'prop-types';
import React, { createRef, useEffect } from 'react';
import { dateFormat } from '@helpers/dateFormat';
import { isEmptyObject } from '@helpers/isEmptyObject';
import { isEmpty } from '@helpers/isEmpty';
import GenericMessage from '@components/Modules/components/GenericMessage';
import Paragraph from '@components/Paragraph';
import { Header } from '@components/Header';
import { stickyContainer, stickyContent } from './registrationBlock.module.css';

const moduleConstants = require('@helpers/identifyModuleType');

export const RegistrationBlock = ({
  moduleCode,
  moduleTitle,
  presentations,
  studentPricingAreaCode,
  studentSelectedCountryName,
  moduleType,
  currentPresentation,
  changePresentation,
  availability,
  genericMessages,
}) => {
  if (isEmpty(moduleCode)) return null;
  if (isEmpty(moduleTitle)) return null;
  //if (!presentations) return null;
  if (isEmpty(studentPricingAreaCode)) return null;
  if (isEmpty(moduleType)) return null;

  let presentationRows = [];
  if (!isEmptyObject(presentations?.node?.CVPDetails?.CVP)) {
    presentations?.node?.CVPDetails.CVP.map((presentation) => {
      presentationRows.push({
        'Presentation Code': presentation.PresentationCode5,
        'Start Date': dateFormat(presentation.CVPStartDate, '-'),
        NSFED: dateFormat(presentation.NSFED, '-'),
        FED: dateFormat(presentation.FED, '-'),
        REGOPEN: dateFormat(presentation.ReservationOpen, '-'),
        RESOPEN: dateFormat(presentation.ReservationOpen, '-'),
        ClickToRegister: presentation.ClickToRegister,
      });
      return presentationRows;
    });
  }

  const selectedPresStartDate = isEmptyObject(currentPresentation) ? '' : currentPresentation['CVPStartDate'];
  const selectedPresRegistrationEndDate = isEmptyObject(currentPresentation) ? '' : currentPresentation['NSFED'];
  const selectedPresCode = isEmptyObject(currentPresentation) ? '' : currentPresentation['PresentationCode5'];

  const regOpenCount = presentationRows.filter((x) => x.ClickToRegister === 'REGOPEN').length;

  const outis1BDLink = process.env.GATSBY_OUTIS1BD_REGISTRATION_URL;

  const _regLink = [outis1BDLink, '?CourseCode=', moduleCode, '&PresCode=', selectedPresCode].join('');

  const buttonRef = createRef();
  const groupRef = createRef();

  useEffect(() => {
    const handleRegistrationClick = (event) => {
      global.window.localStorage.setItem('courseLink', window.location.href);
      const { target } = event;
      if (target && target === buttonRef.current) {
        window.location.href = _regLink;
      }
    };

    if (buttonRef.current) {
      buttonRef.current.addEventListener('click', handleRegistrationClick);
    }

    return () => {
      if (buttonRef.current) {
        buttonRef.current.removeEventListener('click', handleRegistrationClick);
      }
    };
  }, [_regLink, buttonRef]);

  useEffect(() => {
    const handleRadioClick = (event) => {
      //  Update the current state of the selected presentation
      if (!isEmpty(event.target.value)) {
        changePresentation(presentations?.node?.CVPDetails.CVP[event.target.value]);
      }
    };

    if (groupRef.current) {
      groupRef.current.addEventListener('click', handleRadioClick);
    }

    return () => {
      if (groupRef.current) {
        groupRef.current.removeEventListener('click', handleRadioClick);
      }
    };
  }, [_regLink, groupRef]);

  return (
    <div className={stickyContainer}>
      <div className={stickyContent}>
        <ou-content-block theme="dark" fill-width>
          <ou-content-area slot="content-body">
            <Header text="Register" level="h3" theme="dark" hasNoMargin />

            {availability ? (
              <>
                {presentations?.node?.CVPDetails?.CVP?.length > 0 && moduleType === moduleConstants.MICROCREDENTIAL && (
                  <Paragraph size="medium" theme="dark" hasNoMargin>
                    Next start date:
                  </Paragraph>
                )}
                {presentations?.node?.CVPDetails?.CVP?.length > 0 &&
                  moduleType === moduleConstants.SHORT_COURSE_CONTINUOUS && (
                    <Paragraph size="medium" theme="dark" hasNoMargin>
                      You can start this course anytime.
                    </Paragraph>
                  )}

                {!(isEmptyObject(presentations) || isEmptyObject(currentPresentation)) ? (
                  <>
                    {moduleType === moduleConstants.MICROCREDENTIAL ? (
                      <>
                        <Paragraph size="medium" theme="dark" hasNoMargin>
                          {dateFormat(selectedPresStartDate)}
                        </Paragraph>
                        <Paragraph size="medium" theme="dark" hasNoMargin>
                          Register by {dateFormat(selectedPresRegistrationEndDate)}
                        </Paragraph>
                      </>
                    ) : moduleType === moduleConstants.SHORT_COURSE_CONTINUOUS ? (
                      <Paragraph size="medium" theme="dark">
                        Registration closes {dateFormat(selectedPresRegistrationEndDate)}
                      </Paragraph>
                    ) : (
                      <ou-radio-button-group
                        name="radRegistrationDates"
                        layout="vertical"
                        ref={groupRef}
                        theme="dark"
                        label="Choose a start date:"
                      >
                        {presentationRows.map((row, index) =>
                          row['ClickToRegister'] === 'REGOPEN' ? (
                            <ou-radio-button
                              key={`radio-option-${row['Presentation Code']}`}
                              text={dateFormat(row['Start Date'])}
                              value={index}
                              input-id={index}
                              {...(index === 0 && { ['is-checked']: true })}
                              hint-text={GenericMessage(
                                row['ClickToRegister'],
                                moduleCode,
                                row['Presentation Code'].substring(2),
                                BuildReplacementTokens(row),
                                genericMessages,
                                'INNERHTML'
                              )}
                              theme="dark"
                            ></ou-radio-button>
                          ) : (
                            <React.Fragment key={`notregopen-${row['Presentation Code']}`}>
                              <span style={{ color: 'white', fontWeight: 'bold' }}>
                                {dateFormat(row['Start Date'])}
                              </span>
                              {GenericMessage(
                                row['ClickToRegister'],
                                moduleCode,
                                row['Presentation Code'].substring(2),
                                BuildReplacementTokens(row),
                                genericMessages,
                                ''
                              )}
                            </React.Fragment>
                          )
                        )}
                      </ou-radio-button-group>
                    )}
                    {regOpenCount > 0 && (
                      <ou-button ref={buttonRef} text="Register now" size="large" fixed-width theme="dark"></ou-button>
                    )}
                  </>
                ) : (
                  GenericMessage('NO-CURRENT-PRES', moduleCode, '', [], genericMessages, '')
                )}
              </>
            ) : (
              <Paragraph size="medium" theme="dark">
                Unfortunately, this module is not available to students resident in {studentSelectedCountryName}.
              </Paragraph>
            )}
          </ou-content-area>
        </ou-content-block>
      </div>
    </div>
  );
};

function BuildReplacementTokens(row) {
  const replacementTokens = [
    { key: 'FED', value: dateFormat(row['FED']) },
    { key: 'NSFED', value: dateFormat(row['NSFED']) },
    { key: 'RESOPEN', value: dateFormat(row['RESOPEN']) },
    { key: 'COURSE_START', value: dateFormat(row['Start Date']) },
    {
      key: 'ENTRYREQSLINK',
      value: '<ou-link href="#Entry requirements" theme="dark">Entry requirements</ou-link>',
    },
  ];

  return replacementTokens;
}

RegistrationBlock.propTypes = {
  moduleCode: PropTypes.string,
  moduleTitle: PropTypes.string,
  presentations: PropTypes.object,
  studentPricingAreaCode: PropTypes.string,
  studentSelectedCountryName: PropTypes.string,
  moduleType: PropTypes.string,
  currentPresentation: PropTypes.object,
  changePresentation: PropTypes.func,
  availability: PropTypes.bool,
  genericMessages: PropTypes.array,
};
